import React from "react";

import { Chip } from "ui/chip";
import { TableLoader } from "ui/data-grid";
import CampaignPageHeader from "containers/admin/clients/campaign/components/campaign-page-header/campaign-page-header";
import FormPageLayout from "containers/admin/clients/client/components/form-page-layout/form-page-layout";
import CampaignTemplateEditableTitle from "containers/admin/clients/client/campaign-templates-page/campaign-template-editable-title";
import { getProgramTypeLabel } from "containers/admin/clients/client/campaign-templates-page/campaign-template.constants";
import { CampaignTemplateSummaryPanel } from "containers/admin/clients/client/campaign-templates-page/campaign-template-summary-panel";
import { useCampaignStrategyForm } from "containers/admin/clients/campaign/campaign-strategy-page/use-campaign-strategy-form";

const FORM_ID = "campaign-strategy-form";

const CampaignStrategyPage = () => {
  const {
    campaign,
    tabs,
    sidebarItems,
    isSchemaLoading,
    isEditing,
    setIsEditing,
    hasChanges,
    tabRevisions,
    getTabValues,
    handleTabValuesChange,
    handleDiscard,
    handleSubmit,
    handleSaveTitle,
    isSaving,
  } = useCampaignStrategyForm();

  if (isSchemaLoading) {
    return (
      <>
        <div className="shrink-0 pt-3">
          <CampaignPageHeader />
        </div>
        <TableLoader isLoading fullScreen />
      </>
    );
  }

  if (!tabs.length) {
    return (
      <>
        <div className="shrink-0 pt-3">
          <CampaignPageHeader />
        </div>
        <div className="flex flex-1 items-center justify-center p-6 text-gray-600">
          Campaign strategy is not available.
        </div>
      </>
    );
  }

  const renderPageHeader = (editActions: React.ReactNode) => (
    <div className="flex items-start justify-between shrink-0 gap-4">
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <CampaignTemplateEditableTitle
          savedName={campaign.name}
          onSave={handleSaveTitle}
          isSaving={isSaving}
          canEdit={isEditing}
          inputName="campaign-title"
          inputAriaLabel="Campaign title"
          editAriaLabel="Edit campaign title"
        />
        <div className="flex flex-wrap items-center gap-2">
          {campaign.programType && (
            <Chip
              size="small"
              variant="outlined"
              color="primary"
              label={getProgramTypeLabel(campaign.programType)}
            />
          )}
          {campaign.transferStudentStatusFlag && (
            <Chip
              size="small"
              variant="outlined"
              color="success"
              label="Transfer campaign"
            />
          )}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-3">
        {editActions}
        {!isEditing && (
          <CampaignTemplateSummaryPanel
            source={{
              type: "data",
              name: campaign.name,
              schemaVersion: campaign.schemaVersion,
              data: campaign.strategyData,
            }}
          />
        )}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="shrink-0 pt-3">
        <CampaignPageHeader />
      </div>
      <FormPageLayout
        formId={FORM_ID}
        sidebarSectionId="campaign-strategy-nav"
        tabs={tabs}
        sidebarItems={sidebarItems}
        renderPageHeader={renderPageHeader}
        rootClassName="flex flex-col flex-1 min-h-0 pt-4 gap-3"
        getTabValues={getTabValues}
        onTabValuesChange={handleTabValuesChange}
        tabRevisions={tabRevisions}
        isSaveDisabled={!hasChanges}
        isDiscardDisabled={!hasChanges}
        isSaving={isSaving}
        isEditing={isEditing}
        onIsEditingChange={setIsEditing}
        editButtonLabel="Edit strategy"
        onSubmit={handleSubmit}
        onDiscard={handleDiscard}
      />
    </div>
  );
};

export default CampaignStrategyPage;
